import * as THREE from 'three';
import { extractArabicPhonemes, extractEnglishPhonemes, pauseCharacters } from '../phonemes/phoneme-mapping';
import { secureRandomUnit } from '../../common/helpers';

export function isBrowserSupported() {
    const webGLSupport = (() => {
        try {
            const canvas = document.createElement('canvas');
            return !!(
                window.WebGLRenderingContext &&
                (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
            );
        } catch (e) {
            return false;
        }
    })();

    const threeJsSupport = THREE.WebGLRenderer !== undefined;
    return webGLSupport && threeJsSupport;
}

export const isArabic = (text: string): boolean => {
    const arabicRegex = /[\u0600-\u06FF]/g;
    const latinRegex = /[A-Za-z]/g;
    const arabicCount = (text.match(arabicRegex) || []).length;
    const latinCount = (text.match(latinRegex) || []).length;
    return arabicCount > latinCount;
};

// A helper function that builds a timeline of phoneme events based on the transcript.
// Each event has a 'phoneme' and a 'start' time (in seconds relative to speech start).
export function generatePhonemeTimeline(text: string, phonemeRate: number): Array<{ phoneme: string; start: number }> {
    const timeline: Array<{ phoneme: string; start: number }> = [];
    let accumulatedTime = 0;

    // Split the text into tokens (words or punctuation)
    const tokens = text.split(/\s+/).filter(token => token.length > 0);

    // Base duration for one phoneme (in seconds)
    const baseDuration = 1 / phonemeRate;

    tokens.forEach(token => {
        // Check if the token is punctuation only (works for common English and Arabic punctuation)
        if (/^[.,!?؛،]+$/.test(token)) {
            // Calculate pause duration per punctuation character:
            // For a period, use a longer pause (e.g., 0.3s), for others use 0.2s.
            let pauseTime = 0;
            for (let char of token) {
                if (char === '.') {
                    pauseTime += 0.3; // Increase pause for period
                } else {
                    pauseTime += 0.2;
                }
            }
            // Push a neutral/default event and increment the timeline by the total pause duration.
            timeline.push({ phoneme: 'default', start: accumulatedTime });
            accumulatedTime += pauseTime;
        } else {
            // For a normal word, decide if it's Arabic or English and extract phonemes accordingly.
            const isArabicToken = isArabic(token);
            const phonemes = isArabicToken ? extractArabicPhonemes(token) : extractEnglishPhonemes(token);

            phonemes.forEach(p => {
                if (pauseCharacters.includes(p)) {
                    // For any phoneme that is really a pause marker, use a neutral (default) state.
                    timeline.push({ phoneme: 'default', start: accumulatedTime });
                    accumulatedTime += 0.15; // 150ms pause for these markers
                } else {
                    // Introduce slight random variation in duration to simulate human speech variability.
                    const randomFactor = 0.9 + secureRandomUnit() * 0.2;
                    timeline.push({ phoneme: p, start: accumulatedTime });
                    accumulatedTime += baseDuration * randomFactor;
                }
            });
            // Add a short inter-word pause (e.g., 50ms)
            accumulatedTime += 0.05;
        }
    });

    return timeline;
}
